import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient, API_BASE } from '../api/client';

// One assistant shared by the docked panel and the /advisor page: the open
// chat, what's attached as context, and the streaming request. Pages call
// useAssistantRecord() to publish the record they have selected.
const ChatContext = createContext(null);

const PAGE_TITLE = [
  [/^\/$/, 'Dashboard'], [/^\/findings/, 'Findings'], [/^\/security-alerts/, 'Security Alerts'], [/^\/network-map/, 'Network Map'],
  [/^\/assets\/[^/]+/, 'Asset detail'], [/^\/assets/, 'My Assets'], [/^\/agents/, 'Agent Registry'], [/^\/baseline/, 'Baseline Management'],
  [/^\/cases\/[^/]+/, 'Case'], [/^\/cases/, 'Cases'], [/^\/playbooks/, 'Playbooks'], [/^\/response-actions/, 'Response Actions'],
  [/^\/threats/, 'MyCERT Insights'], [/^\/vulnerabilities/, 'Vulnerabilities'], [/^\/advisor/, 'Assistant'],
];
export const pageTitle = (path) => (PAGE_TITLE.find(([re]) => re.test(path)) || [null, null])[1];

const readOpen = () => { try { return localStorage.getItem('assistant_open') === '1'; } catch { return false; } };
const refKey = (r) => `${r.kind}:${r.id}`;

async function* sse(res) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      let event = 'message';
      let data = '';
      chunk.split('\n').forEach(line => {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      });
      if (data) { try { yield { event, data: JSON.parse(data) }; } catch { /* skip malformed */ } }
    }
  }
}

export function ChatProvider({ children }) {
  const location = useLocation();
  const [open, setOpenState] = useState(readOpen);
  const [conversationId, setConversationId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chips, setChips] = useState([]);
  const [pageRecord, setPageRecord] = useState(null);
  const [removed, setRemoved] = useState([]);
  const [live, setLive] = useState(null); // { question, steps } while a reply is in flight
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const abortRef = useRef(null);

  const setOpen = useCallback((v) => {
    setOpenState(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { localStorage.setItem('assistant_open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Ctrl/Cmd+J opens and closes the panel from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const loadConfig = useCallback(() => {
    apiClient.get('/api/assistant/config').then(r => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);
  const loadConversations = useCallback(() => {
    apiClient.get('/api/assistant/conversations?limit=100').then(r => setConversations(r.data?.conversations || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (open || location.pathname.startsWith('/advisor')) { if (!config) loadConfig(); loadConversations(); }
  }, [open, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new page brings a new page record; forget per-page removals.
  useEffect(() => { setRemoved(r => r.filter(k => !k.startsWith('page:'))); }, [location.pathname]);

  const path = location.pathname;
  const page = { path, title: pageTitle(path) || path };
  const pageChip = { kind: 'page', id: path, label: page.title };
  const attached = useMemo(() => {
    const list = [];
    if (!path.startsWith('/advisor') && !removed.includes(refKey(pageChip))) list.push(pageChip);
    if (pageRecord && !removed.includes(refKey(pageRecord)) && !chips.some(c => refKey(c) === refKey(pageRecord))) list.push({ ...pageRecord, fromPage: true });
    return [...list, ...chips];
  }, [path, pageRecord, chips, removed]); // eslint-disable-line react-hooks/exhaustive-deps

  const addChip = useCallback((ref) => {
    setChips(cs => (cs.some(c => refKey(c) === refKey(ref)) ? cs : [...cs, ref]));
    setRemoved(r => r.filter(k => k !== refKey(ref)));
  }, []);
  const removeChip = useCallback((ref) => {
    setChips(cs => cs.filter(c => refKey(c) !== refKey(ref)));
    setRemoved(r => [...r, refKey(ref)]);
  }, []);

  const newChat = useCallback(() => {
    abortRef.current?.abort();
    setConversationId(null); setConversation(null); setMessages([]); setChips([]); setLive(null); setError(null);
  }, []);

  const loadConversation = useCallback(async (id) => {
    abortRef.current?.abort();
    setLive(null); setError(null);
    try {
      const r = await apiClient.get(`/api/assistant/conversations/${id}`);
      setConversationId(id);
      setConversation(r.data.conversation);
      setMessages(r.data.messages || []);
      setChips([]);
    } catch {
      setError('Could not open that chat.');
    }
  }, []);

  const send = useCallback(async (text, { regenerate = false } = {}) => {
    const question = (text || '').trim();
    if ((!question && !regenerate) || live) return;
    setError(null);
    const context = attached.filter(c => c.kind !== 'page').map(({ kind, id, label }) => ({ kind, id, label }));
    const sendPage = attached.some(c => c.kind === 'page');
    const optimistic = regenerate ? null : { id: `local-${Date.now()}`, role: 'user', content: question, meta: { context }, created_at: new Date().toISOString() };
    if (regenerate) setMessages(ms => { const i = ms.map(m => m.role).lastIndexOf('assistant'); return i >= 0 ? ms.slice(0, i) : ms; });
    if (optimistic) setMessages(ms => [...ms, optimistic]);
    setLive({ question: question || messages.filter(m => m.role === 'user').slice(-1)[0]?.content, steps: [], started: Date.now() });

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API_BASE}/api/assistant/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: conversationId, message: question, context, page: sendPage ? page : null, regenerate }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `The assistant returned ${res.status}.`);
      }
      for await (const { event, data } of sse(res)) {
        if (event === 'meta') {
          if (!conversationId) setConversationId(data.conversation_id);
          if (optimistic) setMessages(ms => ms.map(m => (m.id === optimistic.id ? { ...m, id: data.user_message_id } : m)));
        } else if (event === 'tool') {
          setLive(l => l && { ...l, steps: [...l.steps, { ...data, running: true }] });
        } else if (event === 'tool_done') {
          setLive(l => {
            if (!l) return l;
            const steps = [...l.steps];
            const i = steps.map(s => s.name).lastIndexOf(data.name);
            if (i >= 0) steps[i] = { ...data, running: false }; else steps.push(data);
            return { ...l, steps };
          });
        } else if (event === 'message') {
          setMessages(ms => [...ms, data]);
        } else if (event === 'error') {
          throw new Error(data.error || 'The assistant failed.');
        }
      }
      loadConversations();
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'The assistant failed.');
    } finally {
      abortRef.current = null;
      setLive(null);
    }
  }, [attached, conversationId, live, messages, page, loadConversations]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  const deleteConversation = useCallback(async (id) => {
    await apiClient.delete(`/api/assistant/conversations/${id}`);
    if (id === conversationId) newChat();
    loadConversations();
  }, [conversationId, newChat, loadConversations]);

  const pinToCase = useCallback(async (caseId) => {
    if (!conversationId) return;
    await apiClient.patch(`/api/assistant/conversations/${conversationId}`, { case_id: caseId });
    const r = await apiClient.get(`/api/assistant/conversations/${conversationId}`).catch(() => null);
    if (r) setConversation(r.data.conversation);
    loadConversations();
  }, [conversationId, loadConversations]);

  // Compatibility with the older entry points (Discuss, AI Advisor buttons).
  const openChatWithAlert = useCallback((eventIds, eventType) => {
    (eventIds || []).slice(0, 8).forEach(id => addChip({ kind: eventType === 'cve' ? 'cve' : 'ioc', id: String(id), label: `#${id}` }));
    setOpen(true);
  }, [addChip, setOpen]);
  const openChatWithServer = useCallback((serverId, serverName) => {
    addChip({ kind: 'asset', id: serverId, label: serverName || serverId });
    setOpen(true);
  }, [addChip, setOpen]);
  const openChat = useCallback(() => setOpen(true), [setOpen]);

  const value = {
    open, setOpen, openChat, openChatWithAlert, openChatWithServer,
    conversationId, conversation, messages, live, error, setError, config, conversations,
    attached, addChip, removeChip, setPageRecord, page,
    send, stop, newChat, loadConversation, deleteConversation, pinToCase, loadConversations,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within a ChatProvider');
  return ctx;
}

// Publish the record a page has selected, e.g. useAssistantRecord(selected && { kind: 'finding', id, label }).
export function useAssistantRecord(ref) {
  const { setPageRecord } = useChat();
  const key = ref ? `${ref.kind}:${ref.id}:${ref.label || ''}` : '';
  useEffect(() => {
    setPageRecord(ref || null);
    return () => setPageRecord(null);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}
